import axios from "axios";
import { UNAUTH_ROUTE } from "../common/constants";
import { getLocalStorage, LOCAL_STORAGE_TYPE, setLocalStorage } from "../common/localStorage";
import { clearOnLogout } from "../common/utils";
import { globalStores } from "../GlobalStores";
import history from "../history";
import { dashboardStores } from "../pages/Dashboard/DashboardStores";

const { REACT_APP_BASE_URL, REACT_APP_API_TIMEOUT } = process.env;

const axiosInstance = axios.create({
  baseURL: REACT_APP_BASE_URL,
  timeout: REACT_APP_API_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "Access-Control-Allow-Origin": "*",
  },
});

const noRefreshEndpoint = ["/admin/notification/count-unread"];

let refreshRequest = null;
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config } = error;

    if (noRefreshEndpoint.some((ep) => config?.url?.startsWith(ep))) {
      return Promise.reject(error.response);
    }

    if (error?.response?.status === 401 && !config._retry) {
      config._retry = true;
      try {
        const newAccessToken = await refreshToken();
        config.headers["Authorization"] = newAccessToken;
        return Promise.resolve(axiosInstance(config));
      } catch (e) {
        history.push(dashboardStores.isVP() ? "/login/vpbank" : "/login");
        clearOnLogout();
        return Promise.reject(e);
      }
    }
    return Promise.reject(error.response);
  },
);

export const refreshToken = async () => {
  if (refreshRequest) {
    return refreshRequest;
  }
  const promise = new Promise(async (resolve, reject) => {
    try {
      const response = await axios({
        url: `${REACT_APP_BASE_URL}/admin/auth/refresh-token`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          refreshToken: getLocalStorage(LOCAL_STORAGE_TYPE.USER_REFRESH_TOKEN),
          remember: !!(getLocalStorage(LOCAL_STORAGE_TYPE.REMEMBER) === "1"),
        },
      });
      setLocalStorage(LOCAL_STORAGE_TYPE.USER_REFRESH_TOKEN, response.data.data.refreshToken);
      setLocalStorage(LOCAL_STORAGE_TYPE.USER_TOKEN, response.data.data.token);
      resolve(response.data.data.token);
    } catch (error) {
      const pathname = history.location.pathname + history.location.search;
      if (
        history.location.pathname &&
        !Object.values(UNAUTH_ROUTE).some((item) => history.location.pathname.startsWith(item))
      ) {
        setLocalStorage(LOCAL_STORAGE_TYPE.PRE_PATHNAME, pathname);
      }
      clearOnLogout();
      reject(error);
    } finally {
      // Sau khi kết thúc request, đặt lại refreshRequest thành null để cho phép thực hiện request refresh tiếp theo
      refreshRequest = null;
    }
  });
  // Lưu trữ lại promise để tránh việc thực hiện nhiều request refresh cùng một lúc
  refreshRequest = promise;

  return promise;
};

export const requestUploadImage = async (formData) => {
  return new Promise(async (resolve, reject) => {
    try {
      const token = getLocalStorage(LOCAL_STORAGE_TYPE.USER_TOKEN);
      const authHeader = token ? { Authorization: token } : {};

      const response = await axiosInstance.request({
        data: formData,
        headers: {
          ...authHeader,
          "content-type": "multipart/form-data",
        },
        method: "POST",
        url: "/admin/media-upload",
      });
      if (response.data.responseCode >= 200 && response.data.responseCode <= 299) {
        resolve(response.data);
      } else {
        if (response.data.responseCode === 501) {
          globalStores.showAlert("Warning:", response.data.responseText, "warning");
        } else if (
          response.data.responseCode === 400 ||
          response.data.responseCode === 401 ||
          response.data.responseCode === 403
        ) {
          globalStores.showAlert("Warning:", response.data.responseText, "warning");
          history.push(dashboardStores.isVP() ? "/login/vpbank" : "/login");
        } else {
          globalStores.showAlert("Error:", response.data.responseText, "danger");
        }
        resolve(response.data);
      }
    } catch (e) {
      globalStores.showAlert();
      reject(e);
    }
  });
};

// options.private = true: lưu vào thư mục private của S3 (file có thông tin cá nhân, vd. CSV segment)
export const requestUploadFile = async (formData, options = {}) => {
  return new Promise(async (resolve, reject) => {
    try {
      const token = getLocalStorage(LOCAL_STORAGE_TYPE.USER_TOKEN);
      const authHeader = token ? { Authorization: token } : {};

      const response = await axiosInstance.request({
        data: formData,
        headers: {
          ...authHeader,
          "content-type": "multipart/form-data",
        },
        method: "POST",
        url: options.private ? "/admin/file-upload/private" : "/admin/file-upload",
      });
      if (response.data.responseCode >= 200 && response.data.responseCode <= 299) {
        resolve(response.data);
      } else {
        if (response.data.responseCode === 501) {
          globalStores.showAlert("Warning:", response.data.responseText, "warning");
        } else if (
          response.data.responseCode === 400 ||
          response.data.responseCode === 401 ||
          response.data.responseCode === 403
        ) {
          globalStores.showAlert("Warning:", response.data.responseText, "warning");
          history.push(dashboardStores.isVP() ? "/login/vpbank" : "/login");
        } else {
          globalStores.showAlert("Error:", response.data.responseText, "danger");
        }
        resolve(response.data);
      }
    } catch (e) {
      globalStores.showAlert("Upload file error");
      reject(e);
    }
  });
};

// method: 'GET' | 'POST' | 'PUT' | 'DELETE'
export function requestApi(url, method, data, headers, customInstance = {}) {
  const token = getLocalStorage(LOCAL_STORAGE_TYPE.USER_TOKEN);
  const authHeader = token ? { Authorization: token } : {};
  const config = {
    data,
    headers: {
      ...authHeader,
      ...headers,
    },
    method,
    url,
    ...customInstance,
  };
  if (method === "GET") {
    config.params = { ...data };
  } else {
    config.data = data;
  }

  return new Promise(async (resolve, reject) => {
    try {
      const response = await axiosInstance.request(config);

      // File tải về (export) không có responseCode, lỗi HTTP đã được axios reject ở trên
      if (response.data instanceof ArrayBuffer) {
        resolve(response.data);
        return;
      }

      if (response.data.responseCode >= 200 && response.data.responseCode <= 299) {
        resolve(response.data);
      } else {
        if (response.data.responseCode === 501) {
          globalStores.showAlert("Warning:", response.data.responseText, "warning");
        } else if (
          response.data.responseCode === 400 ||
          response.data.responseCode === 401 ||
          response.data.responseCode === 403
        ) {
          globalStores.showAlert("Warning:", response.data.responseText, "warning");
          history.push(dashboardStores.isVP() ? "/login/vpbank" : "/login");
        } else {
          globalStores.showAlert("Error:", response.data.responseText, "danger");
        }
        resolve(response.data);
      }
    } catch (e) {
      reject(e);
    }
  });
}
